/**
 * The tick poll's cadence, and the invariant that makes it honest.
 *
 * ⛔ WHY THIS IS A MODULE AND NOT A CONSTANT IN `App.svelte`:
 * DESK-n12 amended Decision A (2026-08-12 16:3xZ): the wiring builds NOW, but **the live poll does
 * not flip on** while the Supavisor pool is saturated (15 slots, 14–15 held, 2 of 10 connections
 * REFUSED at CC-v's hand). So this object ships **DARK** — and a dark object is exactly the shape
 * this seat's own book warns about:
 *
 *   ⇒ *** A SWITCH GATING A CODE PATH WITH NO DATA BEHIND IT IS INDISTINGUISHABLE FROM THE WHOLE
 *       FIX. *** (HUDCHAT-f, banked: flipping four `false`s would have shipped a byte-identical
 *       chart and read as success.)
 *
 * The defence is that the cadence DECISION lives here, in something an arm can execute, rather than
 * in a literal inside a `.svelte` file no suite mounts. A reviewer can drive `pollIntervalMs(true)`
 * and see the live value without anyone flipping anything in production.
 *
 * ⛔ AND THE DEFECT THE CADENCE HIDES — measured 2026-08-12, not inferred:
 * `EsChartPanel` receives `tickFresh = (last_price_age_s ?? 999) <= 10`. That flag is computed ONCE
 * PER POLL from the fetched payload and then held for the whole interval. At a 30s poll:
 * ```
 *   t=+0s   real age  2s -> fresh, correct
 *   t=+10s  real age 12s -> STILL RENDERED FRESH, and it is not
 *   t=+29s  real age 31s -> STILL RENDERED FRESH, and it is not
 * ```
 * ⇒ *** THE BAR ASSERTS A FRESHNESS IT DOES NOT HAVE FOR TWO THIRDS OF EVERY WINDOW *** — which is
 * the exact thing the em-dash law exists to prevent (*"do not put a number on screen that the
 * reader will believe"*). This is a CORRECTNESS defect, not a latency complaint, and the cure is
 * the invariant below rather than a smaller number chosen by taste.
 */

/** The freshness budget `EsChartPanel` reports against, in seconds. Mirrors the mount's `<= 10`. */
export const FRESHNESS_BUDGET_S = 10;

/** The cadence the owner's spec asks for ("second"), cleared by BUILDER-gg 2026-08-12 14:57Z. */
export const LIVE_POLL_MS = 3_000;

/** What ships until the desk's flip-on word. The pre-existing cadence; nothing regresses. */
export const DARK_POLL_MS = 30_000;

/**
 * ✅ ON BY DESK WORD 2026-08-13 12:5xZ: *"HUDCHAT-j: BUILD THE FLIP OBJECT, on this row as the desk
 * word your arm demands."* The condition it waited on is discharged — the Supavisor pool cure
 * landed 12:49:57Z (repoint to the direct host; pool 0/3 → 3/3; the spine writer recovered unaided,
 * age 363s → 67s), and the saturation turned out to be Hyperdrive's OWN held connections against
 * pool_size 15.
 *
 * ⛔ THIS WAS OFF BY DESK WORD AND IT IS ON BY DESK WORD. It was never a tuning knob and it is not
 * one now — the arm below asserts the flag AND names the row that authorised it, so a future seat
 * reading `true` can find out who said so rather than assuming it drifted.
 */
export const LIVE_POLL_ENABLED = true;

/** The interval the mount must actually use. */
export function pollIntervalMs(live: boolean = LIVE_POLL_ENABLED): number {
  return live ? LIVE_POLL_MS : DARK_POLL_MS;
}

/** Only a served CLOSED verdict slows a poll. Missing or stale evidence keeps it live. */
export function pollIntervalFor(verdict: string | null | undefined): number {
  if (verdict === "CLOSED") return DARK_POLL_MS;
  return pollIntervalMs();
}

/** The live timer supplies its own cadence; only a served closure needs a skip gate. */
export function pollDue(nowMs: number, lastPollMs: number, verdict: string | null | undefined): boolean {
  return verdict !== "CLOSED" || nowMs - lastPollMs >= DARK_POLL_MS;
}

/**
 * The invariant: a poll may not be slower than the freshness budget it reports against.
 *
 * ⇒ If `intervalMs > budget`, the payload is guaranteed to age past the budget before the next
 * fetch replaces it — so the flag says "fresh" over data that is not. The predicate is about
 * REPRESENTABILITY, not tuning: it asks whether the render CAN be honest at this cadence.
 */
export function pollHonoursFreshnessBudget(
  intervalMs: number,
  budgetS: number = FRESHNESS_BUDGET_S,
): boolean {
  return intervalMs <= budgetS * 1_000;
}

/**
 * What the panel may claim about a payload, given how long ago it was fetched.
 *
 * ⚠ `ageAtFetchS` is the server's own `last_price_age_s`; `sinceFetchMs` is wall-clock since the
 * poll returned. The sum is the REAL age, which is what a reader is being shown.
 * ⛔ Returns `false` on a NEGATIVE real age rather than treating it as very fresh — GK-u's
 * one-sided-bound finding on `a8618c7`: a clock skew makes an arbitrarily stale tick pass a
 * `<= budget` gate, and that is the em-dash law's exact failure case.
 */
export function isStillFresh(
  ageAtFetchS: number | null,
  sinceFetchMs: number,
  budgetS: number = FRESHNESS_BUDGET_S,
): boolean {
  if (ageAtFetchS === null || !Number.isFinite(ageAtFetchS)) return false;
  const realAgeS = ageAtFetchS + sinceFetchMs / 1_000;
  if (realAgeS < 0) return false;
  return realAgeS <= budgetS;
}
